import {createFileRoute, SearchSchemaInput, useNavigate} from '@tanstack/react-router'
import {useEffect, useState} from 'react'
import {apiActionImproved} from '../api'
import PButton from '../components/PButton'
import PInput from '../components/PInput'
import {useJwtAuth} from '../JWTAuthProvider'
import services from '../services'
import {codeSentBy, offersTextInstead} from '../components/codeChannel'

// Landing page for a claim link. The player account already exists (created by an organizer). The
// link only names the account — an organizer may have sent it from their own phone, or it may have
// been forwarded — so claiming takes a code sent to the player's mobile (on WhatsApp first, or by
// text; "Text me instead" asks for a text) — the one on file, or one
// they give when there is none), then a password; on success the player is logged straight in and
// continues normal onboarding. Two kinds of link land here:
//   - the invitation EMAIL's link: uid + signed token + the address it was sent to (`em`);
//   - the invite TEXT's short link: `c`, a code the server resolves to the uid and token. A texted
//     player was added by name only — their account has no real email — so they type one here, or
//     they could never log in again.
export const Route = createFileRoute('/claim')({
    validateSearch: (search: Record<string, unknown> & SearchSchemaInput) => ({
        uid: (search.uid as string) || '',
        token: (search.token as string) || '',
        em: (search.em as string) || '',
        c: (search.c as string) || '',
    }),
    component: ClaimPage,
    head: () => ({meta: [{title: 'Claim your player account | ScorePal'}, {name: 'robots', content: 'noindex'}]}),
})

function ClaimPage() {
    const search = Route.useSearch()
    const {c} = search
    const jwt = useJwtAuth()
    const navigate = useNavigate()

    // The short link is resolved once; the long links carry uid and token themselves.
    const [uid, setUid] = useState(search.uid)
    const [token, setToken] = useState(search.token)
    const [resolving, setResolving] = useState(!!c && !(search.uid && search.token))
    const [resolveError, setResolveError] = useState('')
    // The address on the account: from the emailed link, or from resolving a texted one.
    const [em, setEm] = useState(search.em)
    useEffect(() => {
        if (!c || (search.uid && search.token)) return
        services.players.resolveInvite(c)
            .then((r) => { setUid(r.uid); setToken(r.token); setEm(r.email || '') })
            .catch((e: any) => setResolveError(e?.message || 'This invite link is out of date.'))
            .finally(() => setResolving(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [c])

    // The mobile step. `hint` is where the code went ("•••• 3456"); `askPhone` once the server says
    // there is no mobile on file, so the claimant gives one.
    const [step, setStep] = useState<'mobile' | 'code'>('mobile')
    const [askPhone, setAskPhone] = useState(false)
    const [phone, setPhone] = useState('')
    const [hint, setHint] = useState('')
    // How the code went: 'whatsapp' first, 'sms' when WhatsApp could not take it or they asked.
    const [channel, setChannel] = useState<string>('sms')
    const [code, setCode] = useState('')
    // Their name: the one on file (whatever the organizer typed), for them to put right.
    const [ownName, setOwnName] = useState('')

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(false)

    const linkOk = !!(uid && token)
    // No address came with the link (a text, or a copied invite): ask for one.
    const needsEmail = !em

    const sendCode = async (textInstead = false) => {
        if (askPhone && phone.trim().replace(/\D/g, '').length < 7) { setError('Please enter your mobile number, with the country code.'); return }
        setBusy(true); setError('')
        try {
            const res: any = await apiActionImproved('invite-claim', {player_uid: uid, token, ...(askPhone ? {phone: phone.trim()} : {}), ...(textInstead ? {channel: 'sms'} : {})})
            setHint(res?.phone_hint || phone.trim())
            setChannel(res?.channel || 'sms')
            if (res?.name && !ownName) setOwnName(res.name)
            setStep('code')
        } catch (err: any) {
            const msg = err?.message || 'Could not send the code. Please try again.'
            // No mobile on file: the claimant gives one, and proves it the same way.
            if (/enter your mobile number/i.test(msg)) { setAskPhone(true); setError('') } else { setError(msg) }
        } finally {
            setBusy(false)
        }
    }

    const submit = async (e: any) => {
        e.preventDefault()
        if (step === 'mobile') { await sendCode(); return }
        if (code.replace(/\D/g, '').length < 4) { setError('Enter the code we sent you.'); return }
        if (!ownName.trim()) { setError('Please tell us your name.'); return }
        if (needsEmail && !/^\S+@\S+\.\S+$/.test(email.trim())) { setError('Please enter your email address.'); return }
        if (password.length < 8) { setError('Password must be at least 8 characters.'); return }
        if (password !== confirm) { setError('Passwords do not match.'); return }
        setBusy(true); setError('')
        try {
            const res: any = await apiActionImproved('invite-claim', {player_uid: uid, token, code: code.trim(), password, name: ownName.trim(), ...(askPhone ? {phone: phone.trim()} : {}), ...(needsEmail ? {email: email.trim()} : {})})
            if (res?.error || !res?.access_token) throw new Error(res?.error || res?.message || 'Could not claim the account.')
            jwt.setFieldsFromResponse(res)
            navigate({to: '/profiles/me/'} as any)
        } catch (err: any) {
            setError(err?.message || 'Could not claim the account. The link may be out of date.')
            setBusy(false)
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F6F7FB] px-5 py-10">
            <form onSubmit={submit} className="w-full max-w-md bg-white border border-[#e5e7eb] rounded-2xl p-7 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#082F8A]">ScorePal</p>
                <h1 className="mt-1 font-bebas text-2xl text-[#1a1a2e] font-bold tracking-wide">Claim your player account</h1>

                {resolving ? (
                    <p className="font-inter text-sm text-[#1a1a2e]/70 mt-3">Opening your invite…</p>
                ) : !linkOk ? (
                    <p className="font-inter text-sm text-[#1a1a2e]/70 mt-3">
                        {resolveError || 'This claim link is incomplete — please open the link from your invitation again,'}
                        {' '}or <a href="/login/" className="text-primary underline">log in</a> if you already have an account.
                    </p>
                ) : step === 'mobile' ? (
                    <>
                        <p className="font-inter text-sm text-[#1a1a2e]/70 mt-1">
                            Your profile is ready{em ? <> for <span className="font-semibold">{em}</span></> : null}.
                            First, prove it&apos;s you: we&apos;ll send a code to {askPhone ? 'your mobile' : 'the mobile number your organizer has for you'}, on WhatsApp or by text.
                        </p>

                        <div className="mt-5 space-y-4">
                            {askPhone && (
                                <PInput label="Your mobile" type="tel" placeholder="+44 7400 123456" autoComplete="tel"
                                        description="Include your country code. We'll add it to your account once it's confirmed."
                                        value={phone} onChange={(e) => setPhone(e.target.value)} isRequired autoFocus />
                            )}

                            {error && <p className="text-sm text-danger">{error}
                                {/already (been claimed|exists)/i.test(error) && <> <a href="/login/" className="underline">Log in</a></>}
                            </p>}

                            <PButton type="submit" color="primary" className="w-full" isLoading={busy}>
                                Send me a code
                            </PButton>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="font-inter text-sm text-[#1a1a2e]/70 mt-1">
                            We&apos;ve sent a code {codeSentBy(channel)} to <span className="font-semibold">{hint}</span>. Enter it, choose a
                            password and you&apos;re in. Your matches, schedule and results are waiting.
                        </p>

                        <div className="mt-5 space-y-4">
                            <PInput label="Code" inputMode="numeric" autoComplete="one-time-code" placeholder="123456"
                                    value={code} onChange={(e) => setCode(e.target.value)} isRequired autoFocus />
                            <PInput label="Your name" autoComplete="name" description="As your teammates know you. One name is fine."
                                    value={ownName} onChange={(e) => setOwnName(e.target.value)} isRequired />
                            {em && <PInput label="Email" value={em} isReadOnly isDisabled />}
                            {needsEmail && (
                                <PInput label="Your email" type="email" placeholder="you@example.com" autoComplete="email"
                                        description="You'll use this to log in."
                                        value={email} onChange={(e) => setEmail(e.target.value)} isRequired />
                            )}

                            <PInput label="Password" type="password" placeholder="At least 8 characters"
                                    value={password} onChange={(e) => setPassword(e.target.value)} isRequired />

                            <PInput label="Confirm password" type="password" placeholder="Repeat your password"
                                    value={confirm} onChange={(e) => setConfirm(e.target.value)} isRequired />

                            {error && <p className="text-sm text-danger">{error}
                                {/already (been claimed|exists)/i.test(error) && <> <a href="/login/" className="underline">Log in</a></>}
                            </p>}

                            <PButton type="submit" color="primary" className="w-full" isLoading={busy}>
                                Claim my account
                            </PButton>
                            {/* Not on WhatsApp, or it has not come: the same code step, by text. */}
                            {offersTextInstead(channel) && (
                                <button type="button" onClick={() => sendCode(true)} disabled={busy} className="w-full text-sm font-semibold text-primary underline">
                                    Text me instead
                                </button>
                            )}
                            <button type="button" onClick={() => sendCode()} disabled={busy} className="w-full text-sm text-[#1a1a2e]/60 underline">
                                Send me a new code
                            </button>
                        </div>
                    </>
                )}
            </form>
        </div>
    )
}
